import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const unsplash = (id: string) => `https://images.unsplash.com/photo-${id}?w=900&q=80&auto=format&fit=crop`;

const PHOTOS: SocialPostPreviewMedia[] = [
  '1517836357463-d25dfeac3438',
  '1534438327276-14e5300c3a48',
  '1571019613454-1cb2f99b2d8b',
  '1518611012118-696072aa579a',
  '1544367567-0f2fcb009e0b',
  '1552674605-db6ffd4facb5',
].map((id) => ({ url: unsplash(id), kind: 'image' }));

const VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/23056/23056-720.mp4', kind: 'video' };
const PORTRAIT_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/40246/40246-720.mp4', kind: 'video' };

const PAGE: Pick<SocialPostPreviewCardMetadata, 'network' | 'networkLabel' | 'accountName' | 'accountLabel' | 'accountPictureUrl' | 'labels'> = {
  network: 'facebook',
  networkLabel: 'Facebook',
  accountName: 'Feature Frenzy 2',
  accountLabel: 'Feature Frenzy 2 (Facebook Page)',
  accountPictureUrl: 'https://graph.facebook.com/312593965275377/picture?type=large',
  labels: LABELS,
};

const post = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  ...PAGE,
  media: [],
  mediaCount: 0,
  postType: 'post',
  decisionId: `fb-${id}`,
  taskId: `fb-task-${id}`,
  ...card,
});

export const FACEBOOK_SAMPLES: { id: string; title: string; card: SocialPostPreviewCardMetadata }[] = [
  {
    id: 'real-text',
    title: 'Real held post: text',
    card: post('yoga', {
      decisionId: 'd-yoga',
      taskId: 't-yoga',
      caption:
        "New on Sunday mornings: yoga! Join us every Sunday at 9:00 for a class that welcomes all levels, whether it's your first time on a mat or your hundredth. Your first class is free. Come start your Sunday with a calm, easy flow. 🧘",
    }),
  },
  {
    id: 'real-photo',
    title: 'Real held post: photo',
    card: post('pilates', {
      decisionId: 'd-pilates',
      taskId: 't-pilates',
      caption:
        "Pilates Reformer intro week is here. Join us Monday to Friday at 18:00 and try the Reformer for yourself. Spots are limited, so don't wait. #Pilates #ReformerPilates",
      media: [{ url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200', kind: 'image' }],
      mediaCount: 1,
    }),
  },
  {
    id: 'real-link',
    title: 'Real held post: link',
    card: post('winter', {
      decisionId: 'd-winter',
      taskId: 't-winter',
      caption: 'Staying consistent with your workouts this winter? Read our tips and keep your routine going when the days get shorter.',
      link: 'https://www.wix.com/blog',
    }),
  },
  {
    id: 'text',
    title: 'Facebook · text only',
    card: post('text', {
      caption: 'New class alert! Sunrise HIIT starts Monday at 6:15am. Thirty minutes, all levels, bring water and a friend.',
    }),
  },
  {
    id: 'photo-1',
    title: 'Facebook · one photo',
    card: post('photo-1', {
      caption: 'Saturday strength session was on fire. Thank you to everyone who showed up and lifted heavy!',
      media: PHOTOS.slice(0, 1),
      mediaCount: 1,
    }),
  },
  {
    id: 'photo-2',
    title: 'Facebook · two photos',
    card: post('photo-2', {
      caption: 'Before and after: our new yoga room is finally ready. Come see it this week.',
      media: PHOTOS.slice(0, 2),
      mediaCount: 2,
    }),
  },
  {
    id: 'photo-3',
    title: 'Facebook · three photos',
    card: post('photo-3', {
      caption: 'Three classes, one weekend. Which one are you joining?',
      media: PHOTOS.slice(0, 3),
      mediaCount: 3,
    }),
  },
  {
    id: 'photo-5',
    title: 'Facebook · five photos of six (+2)',
    card: post('photo-5', {
      caption: 'Highlights from our open day. Thank you for coming!',
      media: PHOTOS.slice(0, 5),
      mediaCount: 6,
      labels: { ...LABELS, mediaNotShown: 'Photos or videos not shown here: 1' },
    }),
  },
  {
    id: 'video',
    title: 'Facebook · one landscape video',
    card: post('video', {
      caption: 'Rope day. Ten minutes of skipping, three rounds, no excuses. Who is joining Thursday’s conditioning class?',
      media: [VIDEO],
      mediaCount: 1,
      postType: 'video',
    }),
  },
  {
    id: 'video-portrait',
    title: 'Facebook · one portrait video',
    card: post('video-portrait', {
      caption: 'Box jumps, round two. Our coaches will scale every move to your level.',
      media: [PORTRAIT_VIDEO],
      mediaCount: 1,
      postType: 'video',
    }),
  },
  {
    id: 'video-grid',
    title: 'Facebook · photos and a video',
    card: post('video-grid', {
      caption: 'Conditioning class, start to finish.',
      media: [PHOTOS[0], VIDEO, PHOTOS[1]],
      mediaCount: 3,
    }),
  },
  {
    id: 'reel',
    title: 'Facebook · reel',
    card: post('reel', {
      postType: 'reel',
      caption: 'Box jumps, round two. Scaled for every level, book at https://www.featurefrenzy2.com/hiit #HIIT #boxjumps',
      media: [PORTRAIT_VIDEO],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Reel' },
    }),
  },
  {
    id: 'reel-landscape',
    title: 'Facebook · reel from a landscape video',
    card: post('reel-landscape', {
      postType: 'reel',
      caption: 'Rope day.',
      media: [VIDEO],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Reel' },
    }),
  },
  {
    id: 'story-1',
    title: 'Facebook · story, one photo',
    card: post('story-1', {
      postType: 'story',
      media: [{ url: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=900&h=1600&fit=crop', kind: 'image' }],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Story' },
    }),
  },
  {
    id: 'story-3',
    title: 'Facebook · story, three items',
    card: post('story-3', {
      postType: 'story',
      media: [PHOTOS[4], PORTRAIT_VIDEO, PHOTOS[2]],
      mediaCount: 3,
      labels: { ...LABELS, postType: 'Story' },
    }),
  },
  {
    id: 'link',
    title: 'Facebook · link only',
    card: post('link', {
      caption: 'The November timetable is up. Book your spot before it fills.',
      link: 'https://www.featurefrenzy2.com/schedule',
    }),
  },
  {
    id: 'long',
    title: 'Facebook · long caption with hashtags and a URL',
    card: post('long', {
      caption:
        'We turned two this month, and we could not have done it without you.\n\nWhen we opened Feature Frenzy 2 we had one room, six kettlebells and a playlist that was mostly 90s pop. Today we run 28 classes a week, from Sunrise HIIT to Sunday restorative yoga, and our coaches know most of you by name.\n\nTo celebrate, every class this Saturday is free for members and their friends. Book at https://www.featurefrenzy2.com/birthday and bring someone who has been meaning to start.\n\n#FeatureFrenzy2 #fitness #birthdayweek #hiit #yoga',
      media: PHOTOS.slice(2, 3),
      mediaCount: 1,
    }),
  },
  {
    id: 'scheduled',
    title: 'Facebook · scheduled',
    card: post('scheduled', {
      caption: 'Reminder: the studio is closed on Thursday for deep cleaning. See you Friday!',
      media: PHOTOS.slice(3, 4),
      mediaCount: 1,
      scheduledAt: '2026-10-30T07:30:00.000Z',
    }),
  },
  {
    id: 'hebrew',
    title: 'Facebook · Hebrew caption',
    card: post('hebrew', {
      caption: 'שיעור יוגה חדש בימי שלישי ב-19:00. מתאים לכל הרמות, הביאו מזרן ובקבוק מים. #יוגה #FeatureFrenzy2',
      media: PHOTOS.slice(4, 5),
      mediaCount: 1,
    }),
  },
  {
    id: 'no-picture',
    title: 'Facebook · Page picture that 404s',
    card: post('no-picture', {
      accountPictureUrl: 'https://static.wixstatic.com/media/feature-frenzy-2-missing-logo.png',
      caption: 'Personal training slots for November are open. Message us to book a free intro session.',
    }),
  },
];
